import { useMemo, useState } from "react";
// Pull in via the `/util` subpath, NOT the root barrel — `@hearth/shared`
// re-exports server-only modules (auth/invitationToken.ts uses
// `node:crypto`) and Vite has no browser polyfill, so a barrel import
// breaks the web bundle.
import {
  atrPositionSize,
  fixedFractional,
  kellyFraction,
} from "@hearth/shared/util";
import {
  centsToDollars,
  dollarsToCents,
  formatCentsCurrency,
} from "../lib/util/money.js";

/**
 * Live position-sizing calculator embedded in the chart-page sidebar
 * (Phase 2 Session 2.4).
 *
 * Three modes:
 *   - Kelly: returns the suggested fraction of capital, given win rate
 *     + win/loss ratio. Pure stat: clamped to [0, 1].
 *   - Fixed-fractional: shares = floor(account × risk% / |entry − stop|)
 *   - ATR-based: stop is derived from atr × multiplier (atr is the
 *     latest non-null `atr_14` from the chart's already-fetched data).
 *
 * State is in-memory only (this is a calculator, not a saved tool).
 * Persisting account-value as a household preference is a Phase 4+
 * polish task.
 */

interface PositionSizerProps {
  readonly ticker: string;
  readonly latestClose: number | null;
  readonly latestAtr14: number | null;
}

type Mode = "kelly" | "fixed" | "atr";

export function PositionSizer({
  ticker,
  latestClose,
  latestAtr14,
}: PositionSizerProps) {
  const [mode, setMode] = useState<Mode>("fixed");
  const [accountDollars, setAccountDollars] = useState("100000");
  const [riskPct, setRiskPct] = useState("1");
  const [winRate, setWinRate] = useState("55");
  const [winLossRatio, setWinLossRatio] = useState("2");
  const [stopDollars, setStopDollars] = useState("");
  const [atrMultiplier, setAtrMultiplier] = useState("2");
  const [entryDollars, setEntryDollars] = useState(
    latestClose !== null ? latestClose.toFixed(2) : "",
  );

  const accountCents = dollarsToCents(accountDollars);
  const entryCents = dollarsToCents(entryDollars);
  const stopCents = dollarsToCents(stopDollars);
  const riskFraction = Number(riskPct) / 100;

  const kellyResult = useMemo(() => {
    if (mode !== "kelly") return null;
    const wr = Number(winRate) / 100;
    const ratio = Number(winLossRatio);
    if (!Number.isFinite(wr) || !Number.isFinite(ratio)) return null;
    try {
      const fraction = kellyFraction({ winRate: wr, winLossRatio: ratio });
      const dollars = Math.round(accountCents * fraction);
      return { fraction, dollars };
    } catch {
      return null;
    }
  }, [mode, winRate, winLossRatio, accountCents]);

  const fixedResult = useMemo(() => {
    if (mode !== "fixed") return null;
    if (
      accountCents <= 0 ||
      entryCents <= 0 ||
      stopCents <= 0 ||
      stopCents === entryCents ||
      !Number.isFinite(riskFraction) ||
      riskFraction <= 0
    ) {
      return null;
    }
    try {
      return fixedFractional({
        accountValueCents: accountCents,
        riskPercent: riskFraction,
        entryPriceCents: entryCents,
        stopPriceCents: stopCents,
      });
    } catch {
      return null;
    }
  }, [mode, accountCents, entryCents, stopCents, riskFraction]);

  const atrResult = useMemo(() => {
    if (mode !== "atr") return null;
    if (latestAtr14 === null) return null;
    const multiplier = Number(atrMultiplier);
    if (
      accountCents <= 0 ||
      entryCents <= 0 ||
      !Number.isFinite(multiplier) ||
      multiplier <= 0 ||
      !Number.isFinite(riskFraction) ||
      riskFraction <= 0
    ) {
      return null;
    }
    try {
      return atrPositionSize({
        accountValueCents: accountCents,
        riskPercent: riskFraction,
        atr: latestAtr14,
        atrMultiplier: multiplier,
        entryPriceCents: entryCents,
      });
    } catch {
      return null;
    }
  }, [mode, latestAtr14, accountCents, entryCents, atrMultiplier, riskFraction]);

  return (
    <section
      aria-labelledby="sizer-heading"
      className="rounded-lg border border-zinc-200 p-4 text-sm dark:border-zinc-800"
    >
      <h2 id="sizer-heading" className="mb-3 font-medium">
        Position sizer · {ticker}
      </h2>
      <div className="mb-3 flex flex-wrap gap-2">
        <ModeButton
          label="Fixed-%"
          active={mode === "fixed"}
          onClick={() => setMode("fixed")}
        />
        <ModeButton
          label="ATR"
          active={mode === "atr"}
          onClick={() => setMode("atr")}
        />
        <ModeButton
          label="Kelly"
          active={mode === "kelly"}
          onClick={() => setMode("kelly")}
        />
      </div>

      {/* Common inputs */}
      <div className="mb-3 grid grid-cols-2 gap-2">
        <NumberField
          label="Account ($)"
          value={accountDollars}
          onChange={setAccountDollars}
        />
        <NumberField
          label="Risk %"
          value={riskPct}
          onChange={setRiskPct}
          step="0.1"
        />
      </div>

      {mode === "kelly" && (
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Win rate %"
            value={winRate}
            onChange={setWinRate}
            step="1"
          />
          <NumberField
            label="Avg win / avg loss"
            value={winLossRatio}
            onChange={setWinLossRatio}
            step="0.1"
          />
        </div>
      )}

      {mode === "fixed" && (
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Entry ($)"
            value={entryDollars}
            onChange={setEntryDollars}
          />
          <NumberField
            label="Stop ($)"
            value={stopDollars}
            onChange={setStopDollars}
          />
        </div>
      )}

      {mode === "atr" && (
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Entry ($)"
            value={entryDollars}
            onChange={setEntryDollars}
          />
          <NumberField
            label="ATR multiplier"
            value={atrMultiplier}
            onChange={setAtrMultiplier}
            step="0.1"
          />
          <div className="col-span-2 text-xs text-zinc-500">
            Latest ATR-14:{" "}
            {latestAtr14 === null ? (
              <span className="font-mono">—</span>
            ) : (
              <span className="font-mono">{latestAtr14.toFixed(4)}</span>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      <div className="mt-4 rounded-md bg-zinc-50 p-3 dark:bg-zinc-900">
        {mode === "kelly" && kellyResult && (
          <Result
            primary={`${(kellyResult.fraction * 100).toFixed(2)}% of account`}
            secondary={`≈ ${formatCentsCurrency(kellyResult.dollars)}`}
          />
        )}
        {mode === "fixed" && fixedResult && (
          <Result
            primary={`${fixedResult.shares.toLocaleString()} shares`}
            secondary={`Risk ${formatCentsCurrency(
              fixedResult.totalRiskCents,
            )} · ${centsToDollars(fixedResult.riskPerShareCents)}/share`}
          />
        )}
        {mode === "atr" && atrResult && (
          <Result
            primary={`${atrResult.shares.toLocaleString()} shares`}
            secondary={`Risk ${formatCentsCurrency(
              atrResult.totalRiskCents,
            )} · stop ${centsToDollars(atrResult.riskPerShareCents)}/share`}
          />
        )}
        {mode === "kelly" && !kellyResult && <Hint>Enter win rate and ratio.</Hint>}
        {mode === "fixed" && !fixedResult && (
          <Hint>Enter account, risk %, entry, and stop.</Hint>
        )}
        {mode === "atr" && !atrResult && (
          <Hint>
            {latestAtr14 === null
              ? "Toggle on ATR-14 indicator to enable ATR sizing."
              : "Enter account, risk %, and entry."}
          </Hint>
        )}
      </div>
    </section>
  );
}

function ModeButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "rounded-md bg-brand-500 px-3 py-1 text-xs font-medium text-white"
          : "rounded-md border border-zinc-300 px-3 py-1 text-xs hover:bg-zinc-50 dark:border-zinc-700 dark:hover:bg-zinc-900"
      }
    >
      {label}
    </button>
  );
}

function NumberField({
  label,
  value,
  onChange,
  step,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  step?: string;
}) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium text-zinc-600 dark:text-zinc-400">
        {label}
      </span>
      <input
        type="number"
        step={step ?? "any"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-zinc-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-zinc-700 dark:bg-zinc-900"
      />
    </label>
  );
}

function Result({
  primary,
  secondary,
}: {
  primary: string;
  secondary: string;
}) {
  return (
    <div>
      <p className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
        {primary}
      </p>
      <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
        {secondary}
      </p>
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs italic text-zinc-500">{children}</p>
  );
}
